import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { I18nProvider } from '@/lib/i18n';
import { SessionProvider, useSession } from '@/lib/session';
import { TeamsProvider } from '@/lib/teams';
import { ThemeProvider } from '@/lib/theme';
import { ToastProvider } from '@/ui';
import { RouteFallback } from '@/app/RouteFallback';

/**
 * Route map. Four areas share one bundle entry and split at the route level:
 * the marketing site, the signed-in app, the vendor portal and the operator
 * console.
 */

// The public pages are prerendered, so they are imported eagerly: a lazy chunk
// would make the first client render the Suspense fallback instead of the
// markup the server sent, which is a hydration mismatch — and it would delay
// the moment the hero buttons start working.
import Landing from '@/site/Landing';
import Security from '@/site/Security';
import Contact from '@/site/Contact';
import Legal from '@/site/Legal';
import NotFound from '@/site/NotFound';

const AppShell = lazy(() => import('@/app/AppShell'));
const AuthScreen = lazy(() => import('@/app/screens/AuthScreen'));
const InviteScreen = lazy(() => import('@/app/screens/InviteScreen'));
const ResetScreen = lazy(() => import('@/app/screens/ResetScreen'));
const VerifyScreen = lazy(() => import('@/app/screens/VerifyScreen'));
const HomeScreen = lazy(() => import('@/app/screens/HomeScreen'));
const NewEvaluation = lazy(() => import('@/app/screens/NewEvaluation'));
const DraftScreen = lazy(() => import('@/app/screens/DraftScreen'));
const TeamScreen = lazy(() => import('@/app/screens/TeamScreen'));
const SettingsScreen = lazy(() => import('@/app/screens/SettingsScreen'));
const InstanceAdminScreen = lazy(() => import('@/app/screens/InstanceAdminScreen'));
const ProjectScreen = lazy(() => import('@/app/screens/ProjectScreen'));
const ReportView = lazy(() => import('@/app/views/ReportView'));
const PresentView = lazy(() => import('@/app/views/PresentView'));

// The Microsoft Teams surfaces: a popup sign-in, the tab configuration page and
// the meeting side panel. Lazy, so a browser visitor never loads the Teams SDK.
const TeamsSignIn = lazy(() => import('@/app/teams/TeamsSignIn'));
const TeamsConfig = lazy(() => import('@/app/teams/TeamsConfig'));
const TeamsPanel = lazy(() => import('@/app/teams/TeamsPanel'));

const EmbedView = lazy(() => import('@/embed/EmbedView'));
const PortalScreen = lazy(() => import('@/portal/PortalScreen'));
const ContributorScreen = lazy(() => import('@/contributor/ContributorScreen'));
const AdminApp = lazy(() => import('@/admin/AdminApp'));

function RequireSession({ children }: { children: React.ReactNode }) {
  const { loading, signedIn } = useSession();
  const location = useLocation();
  if (loading) return <RouteFallback />;
  if (!signedIn) {
    const next = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`/app/signin?next=${next}`} replace />;
  }
  return <>{children}</>;
}

/** The German site mirrors the English one under /de. */
function GermanSite() {
  return (
    <I18nProvider lang="de">
      <Routes>
        <Route index element={<Landing />} />
        <Route path="security" element={<Security />} />
        <Route path="contact" element={<Contact />} />
        <Route path="imprint" element={<Legal page="imprint" />} />
        <Route path="privacy" element={<Legal page="privacy" />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </I18nProvider>
  );
}

export function App() {
  return (
    <ThemeProvider>
      <I18nProvider>
        <ToastProvider>
          <TeamsProvider>
            <SessionProvider>
              <Suspense fallback={<RouteFallback />}>
                <Routes>
                  <Route path="/" element={<Landing />} />
                  <Route path="/security" element={<Security />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/imprint" element={<Legal page="imprint" />} />
                  <Route path="/privacy" element={<Legal page="privacy" />} />
                  <Route path="/de/*" element={<GermanSite />} />

                  <Route path="/app/signin" element={<AuthScreen mode="signin" />} />
                  <Route path="/app/teams/signin" element={<TeamsSignIn />} />
                  <Route path="/app/teams/config" element={<TeamsConfig />} />
                  <Route path="/app/teams/panel" element={<TeamsPanel />} />
                  <Route path="/app/invite/:token" element={<InviteScreen />} />
                  <Route path="/app/reset/:token" element={<ResetScreen />} />
                  <Route path="/app/verify/:token" element={<VerifyScreen />} />

                  <Route
                    path="/app"
                    element={
                      <RequireSession>
                        <AppShell />
                      </RequireSession>
                    }
                  >
                    <Route index element={<HomeScreen />} />
                    <Route path="new" element={<NewEvaluation />} />
                    <Route path="draft" element={<DraftScreen />} />
                    <Route path="team" element={<TeamScreen />} />
                    <Route path="settings" element={<SettingsScreen />} />
                    {/* Requirement 5: gated inside the screen itself by
                        `me.user.tenantAdmin`, not by the route — the same
                        session, no separate sign-in. */}
                    <Route path="instance-admin" element={<InstanceAdminScreen />} />
                    <Route path="p/:projectId" element={<ProjectScreen />} />
                    <Route path="p/:projectId/report" element={<ReportView />} />
                    <Route path="p/:projectId/:view" element={<ProjectScreen />} />
                  </Route>

                  {/* Sibling of the `/app` shell, not nested inside it: a
                      projected screen shows nothing of the app's own
                      navigation, only this route's own dark, chrome-free
                      stage (see PresentView). A literal "present" segment
                      outranks the shell's own dynamic `p/:projectId/:view`
                      route in React Router's route scoring, so this always
                      wins the match regardless of order. */}
                  <Route
                    path="/app/p/:projectId/present"
                    element={
                      <RequireSession>
                        <PresentView />
                      </RequireSession>
                    }
                  />

                  <Route path="/embed/:token" element={<EmbedView />} />
                  <Route path="/portal/:token" element={<PortalScreen />} />
                  {/* Sibling of /app, not nested inside it — same reasoning
                      as /app/p/:projectId/present above: a contributor
                      holding this link is not inside a workspace and must
                      see none of its navigation (HANDOVER.md "no app shell
                      navigation"). */}
                  <Route path="/contributor/:token" element={<ContributorScreen />} />
                  <Route path="/admin/*" element={<AdminApp />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </SessionProvider>
          </TeamsProvider>
        </ToastProvider>
      </I18nProvider>
    </ThemeProvider>
  );
}

export default App;
